import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import Header from "layouts/pages/profile/components/Header2";
import { AuthContext } from "context";
import { productService } from "services/productService";

function DiscoverSAAS() {
  const { partnerSettings, customerPrices, user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    productService.getProducts("saas").then((all) => {
      const allowedProducts = user?.isPartner
        ? partnerSettings?.effective_enabled_products
        : partnerSettings?.enabled_products;
      const partnerCustomPrices = user?.isPartner
        ? (partnerSettings?.effective_customer_custom_prices || partnerSettings?.custom_prices)
        : partnerSettings?.custom_prices;
      const partnerCustomNames = user?.isPartner
        ? (partnerSettings?.effective_customer_custom_names || partnerSettings?.custom_names)
        : partnerSettings?.custom_names;

      const filtered = allowedProducts
        ? all.filter((p) => allowedProducts.includes(p.slug))
        : all;
      // Preise/Namen anwenden: Kundenpreis > Partnerpreis > Standard
      const withCustomizations = filtered.map((p) => {
        const cp = customerPrices && customerPrices[p.slug];
        const finalPrice = (cp && cp.custom_price) || partnerCustomPrices?.[p.slug];
        const finalName  = (cp && cp.custom_name)  || partnerCustomNames?.[p.slug];
        if (!finalPrice && !finalName) return p;
        return Object.assign({}, p,
          finalName  ? { name: finalName } : {},
          finalPrice ? { display_price: finalPrice } : {}
        );
      });
      setProducts(withCustomizations);
    }).catch(() => {
      setProducts([]);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [partnerSettings, customerPrices, user]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <Header />
        <SoftBox pt={5} pb={2} display="flex" justifyContent="center">
          <CircularProgress />
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header />
      <SoftBox pt={5} pb={2}>
        {products.length === 0 ? (
          <SoftTypography variant="body2" color="text">
            Keine SaaS-Produkte verfügbar.
          </SoftTypography>
        ) : (
          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} md={6} lg={4} key={product.id}>
                <SoftBox
                  p={2}
                  border="1px solid #e2e8f0"
                  borderRadius="12px"
                  sx={{
                    backgroundColor: "#ffffff",
                    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)",
                    transition: "transform 0.3s, box-shadow 0.3s",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 10px 20px 0 rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <SoftTypography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                      color: "#1a202c",
                      textDecoration: "none",
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "1.25rem",
                    }}
                    gutterBottom
                  >
                    {product.name}
                  </SoftTypography>
                  <SoftTypography
                    variant="body2"
                    sx={{
                      color: "#4a5568",
                      textDecoration: "none",
                      display: "block",
                      marginBottom: "16px",
                      fontWeight: "400",
                      fontSize: "1rem",
                      minHeight: "60px",
                    }}
                    gutterBottom
                  >
                    {product.description}
                  </SoftTypography>
                  {product.display_price && (
                    <SoftTypography
                      variant="subtitle1"
                      sx={{
                        color: "#4B0082",
                        textDecoration: "none",
                        display: "block",
                        marginBottom: "16px",
                        fontWeight: "600",
                        fontSize: "1.1rem",
                      }}
                      gutterBottom
                    >
                      {product.display_price}
                    </SoftTypography>
                  )}
                  {product.learn_more_link && (
                    <SoftTypography
                      variant="body2"
                      component="a"
                      href={product.learn_more_link}
                      sx={{
                        textDecoration: "none",
                        display: "block",
                        marginBottom: "16px",
                        color: "#4B0082",
                        fontWeight: "500",
                        transition: "color 0.3s",
                        "&:hover": {
                          color: "#8b0000",
                        },
                      }}
                    >
                      Mehr erfahren
                    </SoftTypography>
                  )}
                  {product.order_link && (
                    <Button
                      variant="contained"
                      href={product.order_link}
                      target={product.order_link.startsWith("http") ? "_blank" : "_self"}
                      sx={{
                        backgroundImage: ({ functions: { rgba, linearGradient } }) =>
                          `${linearGradient(
                            rgba("#8b0000", 0.9),
                            rgba("#4B0082", 0.9)
                          )}`,
                        color: "#ffffff",
                        fontWeight: "bold",
                        padding: "10px 16px",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "1rem",
                        transition: "all 0.3s",
                        "&:hover": {
                          backgroundImage: ({ functions: { rgba, linearGradient } }) =>
                            `${linearGradient(
                              rgba("#4B0082", 0.9),
                              rgba("#2A0033", 0.9)
                            )}`,
                          boxShadow: "0 4px 12px rgba(75, 0, 130, 0.3)",
                        },
                      }}
                      fullWidth
                    >
                      Jetzt bestellen
                    </Button>
                  )}
                </SoftBox>
              </Grid>
            ))}
          </Grid>
        )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DiscoverSAAS;
